import { Directive, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output } from '@angular/core';

/**
 * Mouse control for a document preview:
 *  - the wheel scrolls, as everywhere else (pages of a PDF go by as usual);
 *  - the pointer is a magnifying glass: click zooms in on that spot, Shift- or
 *    Alt-click zooms out (the pointer shows which);
 *  - Ctrl/Cmd + wheel, and a trackpad pinch (which browsers send as Ctrl + wheel),
 *    zoom around the cursor;
 *  - drag moves the page around. A drag is not a click, so it never zooms.
 *
 * It does not scale anything itself. The host owns the zoom level (a PDF has to
 * re-render at it, an image just grows), so it asks for a step through
 * `zoomStep` and then scrolls to keep the point under the cursor where it was.
 *
 * `scrollSelector` names the element that actually scrolls, when that is not
 * the host — ng2-pdf-viewer scrolls its own inner container.
 */
@Directive({
  selector: '[appPanZoom]'
})
export class PanZoomDirective implements OnDestroy {
  @Input() scrollSelector: string = '';
  /** +1 to zoom in a step, -1 to zoom out one. */
  @Output() zoomStep = new EventEmitter<number>();
  /** Kept for hosts that bind it; nothing emits it now that click zooms. */
  @Output() zoomReset = new EventEmitter<void>();

  private dragging = false;
  private moved = false;
  private startX = 0;
  private startY = 0;
  private startLeft = 0;
  private startTop = 0;
  private lastWheel = 0;
  private anchorFrame: number = null;
  /** How the last press came in. A tap is a click too, and on a phone or tablet, tapping to
   *  scroll or to dismiss something zoomed the page in a step every time. */
  private pointerType = 'mouse';

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly zone: NgZone) {
    host.nativeElement.classList.add('pan-zoom');
    zone.runOutsideAngular(() => {
      host.nativeElement.addEventListener('mousemove', this.onModifier);
      document.addEventListener('keydown', this.onModifier);
      document.addEventListener('keyup', this.onModifier);
    });
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('mousemove', this.onModifier);
    document.removeEventListener('keydown', this.onModifier);
    document.removeEventListener('keyup', this.onModifier);
    this.stopDrag();
    if (this.anchorFrame) cancelAnimationFrame(this.anchorFrame);
  }

  private get scroller(): HTMLElement {
    const el = this.host.nativeElement;
    return (this.scrollSelector && el.querySelector(this.scrollSelector) as HTMLElement) || el;
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    this.pointerType = event.pointerType || 'mouse';
  }

  // ── Drag to pan ──────────────────────────────────────────────────────────

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    // Leave real controls (links, buttons, inputs) clickable.
    if (target.closest('a, button, input, select, textarea')) return;

    const scroller = this.scroller;
    this.dragging = true;
    this.moved = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startLeft = scroller.scrollLeft;
    this.startTop = scroller.scrollTop;
    this.host.nativeElement.classList.add('pan-zoom--dragging');
    // Otherwise the browser starts selecting text or dragging the image out.
    event.preventDefault();

    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    });
  }

  private onMouseMove = (event: MouseEvent) => {
    if (!this.dragging) return;
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) this.moved = true;
    const scroller = this.scroller;
    scroller.scrollLeft = this.startLeft - dx;
    scroller.scrollTop = this.startTop - dy;
  };

  private onMouseUp = () => this.stopDrag();

  private stopDrag(): void {
    this.dragging = false;
    this.host.nativeElement.classList.remove('pan-zoom--dragging');
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  /** Click zooms in on the spot; Shift/Alt-click zooms out. A drag is not a click. */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.moved) {
      event.preventDefault();
      event.stopPropagation();
      this.moved = false;
      return;
    }
    if (event.button !== 0) return;
    // Touch scrolls and pinches natively; only a mouse click is the magnifier.
    if (this.pointerType !== 'mouse') return;
    if ((event.target as HTMLElement).closest('a, button, input, select, textarea')) return;
    event.preventDefault();
    this.zoomAt(event.clientX, event.clientY, event.shiftKey || event.altKey ? -1 : 1);
  }

  // Shift/Alt turns the magnifier to "zoom out" while it is held. Listened to
  // outside Angular: it only swaps a class, so no change detection is needed.
  private onModifier = (event: KeyboardEvent | MouseEvent) =>
    this.host.nativeElement.classList.toggle('pan-zoom--out', event.shiftKey || event.altKey);

  // ── Wheel to zoom ────────────────────────────────────────────────────────

  /** Plain wheel scrolls. Ctrl/Cmd + wheel — and a pinch, which arrives as one — zooms. */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    if (!event.deltaY || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();

    // A trackpad fires dozens of wheel events per gesture; one step per 90ms
    // keeps a flick from jumping straight to the limit (and a PDF from
    // re-rendering for every one of them).
    const now = Date.now();
    if (now - this.lastWheel < 90) return;
    this.lastWheel = now;

    this.zoomAt(event.clientX, event.clientY, event.deltaY < 0 ? 1 : -1);
  }

  private zoomAt(clientX: number, clientY: number, direction: number): void {
    const scroller = this.scroller;
    const rect = scroller.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;
    const width = scroller.scrollWidth || 1;
    const height = scroller.scrollHeight || 1;
    // Where the cursor is, as a fraction of the whole document.
    const fx = (scroller.scrollLeft + offsetX) / width;
    const fy = (scroller.scrollTop + offsetY) / height;

    this.zoomStep.emit(direction);
    this.keepAnchor(fx, fy, offsetX, offsetY, width, height);
  }

  /** Once the content has resized to the new zoom, scroll the same point of the
   *  document back under the cursor. A PDF re-renders asynchronously, so wait
   *  for its size to change (for up to half a second). */
  private keepAnchor(fx: number, fy: number, offsetX: number, offsetY: number, oldWidth: number, oldHeight: number): void {
    if (this.anchorFrame) cancelAnimationFrame(this.anchorFrame);
    let frames = 0;

    this.zone.runOutsideAngular(() => {
      const tick = () => {
        const scroller = this.scroller;
        const resized = scroller.scrollWidth !== oldWidth || scroller.scrollHeight !== oldHeight;
        if (resized || ++frames > 30) {
          scroller.scrollLeft = fx * scroller.scrollWidth - offsetX;
          scroller.scrollTop = fy * scroller.scrollHeight - offsetY;
          this.anchorFrame = null;
          return;
        }
        this.anchorFrame = requestAnimationFrame(tick);
      };
      this.anchorFrame = requestAnimationFrame(tick);
    });
  }
}
